/**
 * StoreFinder Component
 *
 * This component allows users to find a store based on their location.
 * It features a search input for town or postcode, a tabbed navigation
 * to switch between a store list and a map view, and displays a list
 * of stores based on the selected criteria. The component utilizes
 * React hooks for state management and ensures accessibility with
 * appropriate ARIA roles and attributes.
 *
 * Dependencies:
 * - InStoreService: Displays a map view of stores.
 * - StoresList: Lists available stores.
 * - FindStore: Provides functionality to search for stores.
 * - Heroicons: Provides the search icon.
 * - Commercetools UI components: Used for buttons and typography.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import InStoreService from './inStoreService';
import StoresList from './storeList';
import FindStore, { calculateDistanceInMiles } from './findStore';
import Button from 'components/commercetools-ui/atoms/button';
import Typography from 'components/commercetools-ui/atoms/typography';
import NeighborhoodDiscovery, { findMatchingStore, getSuggestions } from './NeighborhoodDiscovery';
import { useRouter } from 'next/navigation';
import { FrontasticImage } from 'frontastic/lib/image';
import { Store } from 'shared/types/product/store';
import { usePathname } from 'next/navigation';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useLocale from 'helpers/hooks/useLocale';
import { desktop, mobile, ultraTablet, tablet, smallMobile } from 'helpers/utils/screensizes';

import CloseIcon from 'public/icons/ag-icons/Close.svg';
import StoreSearchSuggestion from './storeSearchSuggestion';

type InStoreServiceCard = {
  title?: string;
  description?: string;
  imageUrl?: FrontasticImage;
  ctaLink?: string;
  ctaName?: string;
};
// Define StoreDataArray as an array of StoreData
export type StoreDataArray = Store[];

// Define the props for the StoreFinder component
export interface StoreFinderProps {
  storeData: StoreDataArray;
  cardData: InStoreServiceCard[];
  badges: any;
  icons: any[];
  amplienceStores?: any;
  storeTimingsData?: any;
}

type selectedPlace = {
  id?: string;
  name: string;
  location: {
    lat: number;
    lng: number;
  };
};

const StoreFinder = ({ storeData, cardData, badges, icons, amplienceStores, storeTimingsData }: StoreFinderProps) => {
  const [activeTab, setActiveTab] = useState<string>('findStore');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<selectedPlace | undefined>();
  const router = useRouter();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const originalSearchQuery = useRef<string>('');
  // const [nearbyStores, setNearByStores] = useState();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState('');
  const [preservedQuery, setPreservedQuery] = useState<string>('');
  const [mapCenter, setMapCenter] = useState<any>();
  const [queryStore, setQueryStore] = useState('');
  const [suggestions, setSuggestions] = useState<Store[]>([]);
  const [mapZoom, setMapZoom] = useState(5);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMobile] = useMediaQuery(mobile);
  const [isTablet] = useMediaQuery(tablet);
  const [isSmallMobile] = useMediaQuery(smallMobile);
  const { locale, getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';
  const hardcodedLocation = { lat: 53.4808, lng: -2.2426 };
  const [nearbyStores, setNearbyStores] = useState<Store[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [isCitySearch, setIsCitySearch] = useState<boolean>(false);
  const [filteredStores, setFilteredStores] = useState<Store[] | null>(null);
  const [currentLocationClicked, setCurrentLocationClicked] = useState<boolean>(false);

  useEffect(() => {
    // 🔹 Hardcoded location for testing (London, UK)
    // const ukLocation = {
    //   lat: 51.5074, // London's latitude
    //   lng: -0.1278, // London's longitude
    // };

    // setUserLocation(ukLocation);

    // If later you want geolocation back, uncomment:

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const location = {
          lat: pos?.coords?.latitude,
          lng: pos?.coords?.longitude,
        };
        setUserLocation(location);
      },
      (err) => {
        console.warn('Geolocation blocked, using fallback', err);
      },
    );
  }, []);

  let query = '';
  if (pathname?.startsWith('/find-stores')) {
    const searchParams = new URLSearchParams(window?.location?.search);
    query = searchParams?.get('q') || '';
  }

  const getNearbyStores = (selectedPlace: selectedPlace | undefined, storeData: any[]) => {
    if (!selectedPlace) return [];

    return storeData
      .filter((store) => store?.ID !== selectedPlace?.id) // exclude the selected store
      .map((store) => ({
        ...store,
        distance: calculateDistanceInMiles(
          selectedPlace?.location?.lat,
          selectedPlace?.location?.lng,
          store?.latitude,
          store?.longitude,
        ),
      }))
      .sort((a, b) => a?.distance - b?.distance); // nearest → farthest
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setPreservedQuery('');
    setSuggestions([]);
    setSelectedPlace(undefined);
    setNearbyStores([]);
    setQueryStore('');
    setMapZoom(0);
    setIsOpen(false);
    setIsCitySearch(false);
    setFilteredStores(null);
    if (pathname?.startsWith('/find-stores')) {
      window.history.pushState(null, '', '/find-stores');
    }
  };

  const handelSelectedPlace = useCallback(
    (selectedPlace: selectedPlace, originalQuery?: string) => {
      setIsOpen(true);
      setSelectedPlace(selectedPlace);
      setMapCenter(selectedPlace?.location);
      setMapZoom(14);
      // Exit city mode on explicit store selection and compute nearby stores
      if (isCitySearch) {
        setIsCitySearch(false);
        setFilteredStores(null);
      }
      const storesWithDistance = getNearbyStores(selectedPlace, storeData);
      setNearbyStores(storesWithDistance);
      const queryToUse = originalQuery || selectedPlace?.name;
      if (originalQuery) {
        originalSearchQuery.current = originalQuery;
      }
      window.history.pushState(null, '', `/find-stores?q=${encodeURIComponent(queryToUse)}`);
      // router.replace(`/find-stores?q=${encodeURIComponent(queryToUse)}`);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 0);
    },
    [isCitySearch, storeData],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchInput(newValue);
    if (preservedQuery && newValue !== preservedQuery) {
      setPreservedQuery('');
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = searchInput?.trim();
    if (!input) return;

    // Try to match by city name (case-insensitive)
    const cityMatches = storeData?.filter((s) => s?.city?.trim()?.toLowerCase() === input?.toLowerCase());
    const sortCityMatchedWithUserLocation = cityMatches.map(
      (s: any) =>
        ({
          ...s,
          distance:
            s?.latitude != null && s?.longitude != null && userLocation
              ? calculateDistanceInMiles(userLocation.lat, userLocation.lng, s.latitude, s.longitude)
              : null,
        } as Store & { distance?: number | null }),
    );
    // Sort by distance if we have userLocation, otherwise sort alphabetically by name
    sortCityMatchedWithUserLocation?.sort((a: any, b: any) => {
      if (!userLocation) {
        return (a?.name || '')?.localeCompare(b?.name || '');
      }
      return (a?.distance ?? Number.POSITIVE_INFINITY) - (b?.distance ?? Number.POSITIVE_INFINITY);
    });

    if (cityMatches && cityMatches?.length > 0) {
      // ✅ City search mode
      setIsCitySearch(true);
      setFilteredStores(sortCityMatchedWithUserLocation);
      setIsOpen(false); // Ensure overlay is closed initially
      setNearbyStores([]);
      setMapZoom(0);
      window.history.pushState(null, '', `/find-stores?q=${encodeURIComponent(input)}`);
      return;
    }

    const matchedStore = findMatchingStore(storeData, input);
    if (matchedStore) {
      setIsCitySearch(false);
      setFilteredStores(null);
      if (input !== matchedStore.name) {
        setPreservedQuery(input);
      } else {
        setPreservedQuery('');
      }
      const newCenter = { lat: matchedStore?.latitude, lng: matchedStore?.longitude };
      setMapCenter(newCenter);
      setMapZoom(14);
      handelSelectedPlace({ id: matchedStore?.ID, name: matchedStore?.name, location: newCenter }, input);
    }
  };
  const getSVGElement = (iconName: string) => {
    if (!icons || !iconName) return null;

    const iconElement = icons.find((icn) => icn?.iconName === iconName);
    if (!iconElement) return null;

    const asset = iconElement?.asset?.[selectedLocale];
    if (!asset) return null;

    // consistent static URL (Amplience recommended format)
    const svgUrl = `https://americangolf.a.bigcontent.io/v1/static/${asset?.name}`;

    return {
      svgUrl,
      iconElement,
    };
  };
  const searchIcon = getSVGElement('Search Icon');
  const clickAndCollect = getSVGElement('click and collect');
  const mapPin = getSVGElement('Map Pin');

  const getStoreBadges = (store: any) => {
    if (!amplienceStores || !badges) return [];
    // Find the amplience store that matches the current store by ID/storeKey
    const amplienceStore = Array.isArray(amplienceStores)
      ? amplienceStores.find((s: any) => s?.storeKey === store?.ID)
      : null;
    if (!amplienceStore || !amplienceStore?.storeBadges) return [];

    // Map badge IDs to badge objects
    const now = new Date();
    const matchedBadges = amplienceStore?.storeBadges
      .map((badgeId: string) => badges?.find((badge: any) => badge?.id === badgeId))
      .filter(Boolean);

    // Filter out hidden badges and those outside valid date range
    const visibleBadges = matchedBadges?.filter((badge: any) => {
      const { isHidden, validFrom, validTo } = badge.conditions || {};
      if (isHidden) return false;
      const from = validFrom ? new Date(validFrom) : null;
      const to = validTo ? new Date(validTo) : null;
      return (!from || from <= now) && (!to || to >= now);
    });

    // Sort by ranking
    const sortedBadges = visibleBadges?.sort((a: any, b: any) => (a?.ranking ?? 0) - (b?.ranking ?? 0));

    return sortedBadges;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    setQueryStore(value);

    if (value?.trim()?.length > 0) {
      const filtered = getSuggestions(value, storeData, userLocation);
      setSuggestions(filtered?.slice(0, 5)); // show top 5 suggestions
    } else {
      setSuggestions([]);
    }
  };

  const storesWithin50km = nearbyStores.filter((store: any) => store?.distance && store?.distance <= 50)?.length;

  return (
    <>
      {/* mobile view */}
      <div className={`relative flex w-full flex-col gap-16 bg-white  ${isTablet ? 'hidden' : 'block'}`}>
        {/* Heading */}
        <div className="flex flex-col gap-12">
          <Typography className="font-body text-24 font-bold leading-[100%]">Find a Store</Typography>

          {/* Search Input with Icon  */}
          <form onSubmit={handleSearch} className="relative">
            <div
              className={`flex min-h-[50px] items-stretch gap-12  rounded-sm border border-[#303030] bg-white  px-12 py-8`}
            >
              <div className="flex flex-1 items-center gap-8">
                {searchIcon?.iconElement && searchIcon?.svgUrl && (
                  <img
                    src={searchIcon?.svgUrl}
                    alt={searchIcon?.iconElement?.iconName || 'Search Icon'}
                    style={{
                      width: isDesktopSize
                        ? `${searchIcon?.iconElement?.iconWidthDesktopPx}px`
                        : `${searchIcon?.iconElement?.iconWidthMobilePx}px`,
                      height: isDesktopSize
                        ? `${searchIcon?.iconElement?.iconHeightDesktopPx}px`
                        : `${searchIcon?.iconElement?.iconHeightMobilePx}px`,
                    }}
                  />
                )}
                <input
                  type="text"
                  placeholder="Search..."
                  className=" flex-1 border-none text-16 font-normal leading-[100%]"
                  value={searchInput}
                  onChange={(e) => {
                    handleInputChange(e);
                    handleChange(e);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSuggestions([]);
                    }
                  }}
                />
              </div>
              <div className="p-12">
                <CloseIcon className="h-13 w-12 cursor-pointer" onClick={handleClearSearch} />
              </div>
            </div>
            <StoreSearchSuggestion
              storeData={storeData}
              onSelect={(store: any) =>
                handelSelectedPlace({
                  id: store?.ID,
                  name: store?.name,
                  location: { lat: store?.latitude, lng: store?.longitude },
                })
              }
              isMobile={isMobile}
              isSmallMobile={isSmallMobile}
              handleChange={handleChange}
              query={query}
              setQuery={setQueryStore}
              suggestions={suggestions}
              setSuggestions={setSuggestions}
              mapPin={mapPin}
              isDesktop={isDesktopSize}
              userlocation={userLocation}
            />
          </form>
        </div>

        {/* Subtitle / Info */}
        {((storesWithin50km > 0 && !currentLocationClicked && userLocation && nearbyStores?.length > 0) ||
          (storesWithin50km > 0 && currentLocationClicked && selectedPlace && nearbyStores?.length > 0)) && (
          <p className="text-[16px] font-normal leading-[100%] text-[#1E1E1E]">
            {storesWithin50km} Store{storesWithin50km !== 1 ? 's' : ''} Near You
          </p>
        )}
      </div>

      {/* Tab Navigation: Visible only on small to medium screens */}
      {/* Tab Navigation: Visible only on small to medium screens */}
      {/* // Tab buttons - only show on mobile */}
      <div className={`mb-4 mt-12 flex justify-start gap-x-8  ${isTablet ? 'hidden' : 'block'}`}>
        <button
          type="button"
          onClick={() => setActiveTab('findStore')}
          className={`rounded-full border px-16 py-12 text-16 font-bold  leading-[100%] text-black transition ${
            activeTab === 'findStore' ? 'bottom-2 border-[#000]' : 'border-[#E4E4E4]'
          }`}
        >
          LIST
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('map')}
          className={`rounded-full border px-16 py-12 text-16 font-bold leading-[100%] transition ${
            activeTab === 'map' ? 'bottom-2 border-[#000]' : 'border-[#E4E4E4]'
          }`}
        >
          MAP
        </button>
      </div>

      <div className={`mb-[4rem] flex h-[100vh] justify-between rounded-md ${isTablet ? 'flex-row' : 'flex-col'}`}>
        <div
          className={`${activeTab === 'findStore' && 'block '} ${isTablet && 'w-[380px]'} space-y-8 overflow-y-hidden ${
            isDesktopSize ? 'flex min-w-[370px]  max-w-[414px] flex-col' : 'flex-col'
          }`}
        >
          <div
            className={`w-full gap-16 bg-white px-12 pb-16 pt-24 ${isTablet ? 'flex max-w-md ' : 'hidden'} flex-col`}
          >
            <div className="flex flex-col gap-24">
              <Typography className="font-body text-24 font-bold leading-[100%]">Find a Store</Typography>

              <form onSubmit={handleSearch} className="relative">
                <div
                  className={`flex min-h-[50px]  ${isTablet && 'items-center gap-2'} ${
                    isDesktopSize && 'items-stretch gap-12'
                  }rounded-sm border border-[#303030] bg-white  px-12 py-8`}
                >
                  <div className="flex flex-1 items-center gap-8">
                    {searchIcon?.iconElement && searchIcon?.svgUrl && (
                      <img
                        src={searchIcon?.svgUrl}
                        alt={searchIcon?.iconElement?.iconName || 'Search Icon'}
                        style={{
                          width: isDesktopSize
                            ? `${searchIcon?.iconElement?.iconWidthDesktopPx}px`
                            : `${searchIcon?.iconElement?.iconWidthMobilePx}px`,
                          height: isDesktopSize
                            ? `${searchIcon?.iconElement?.iconHeightDesktopPx}px`
                            : `${searchIcon?.iconElement?.iconHeightMobilePx}px`,
                        }}
                      />
                    )}
                    <input
                      type="text"
                      placeholder="Search..."
                      className=" flex-1 border-none text-16 font-normal leading-[100%]"
                      value={searchInput}
                      onChange={(e) => {
                        handleInputChange(e);
                        handleChange(e);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setSuggestions([]);
                        }
                      }}
                    />
                  </div>
                  <div className={`p-12`}>
                    <CloseIcon className="h-13 w-12 cursor-pointer" onClick={handleClearSearch} />
                  </div>
                </div>
                <StoreSearchSuggestion
                  storeData={storeData}
                  onSelect={(store: any) => {
                    handelSelectedPlace({
                      id: store?.ID,
                      name: store?.name,
                      location: { lat: store?.latitude, lng: store?.longitude },
                    });
                  }}
                  isMobile={isMobile}
                  isSmallMobile={isSmallMobile}
                  handleChange={handleChange}
                  query={query}
                  setQuery={setQueryStore}
                  suggestions={suggestions}
                  setSuggestions={setSuggestions}
                  mapPin={mapPin}
                  isDesktop={isDesktopSize}
                  userlocation={userLocation}
                />
              </form>
            </div>
            {((storesWithin50km > 0 && !currentLocationClicked && userLocation && nearbyStores?.length > 0) ||
              (storesWithin50km > 0 && currentLocationClicked && selectedPlace && nearbyStores?.length > 0)) && (
              <p className="text-[16px] font-normal leading-[100%] text-[#1E1E1E]">
                {storesWithin50km} Store{storesWithin50km !== 1 ? 's' : ''} Near You
              </p>
            )}
            {/* <div className="mb-4 px-16">
                      <StoreSearchSuggestion
                        storeData={storeData}
                        onSelect={(store) =>
                         handelSelectedPlace({
                            name: store.name,
                            location: { lat: store.latitude, lng: store.longitude },
                          })
                        }
                        handleChange={handleChange}
                        query={query}
                        setQuery={setQueryStore}
                        suggestions={suggestions}
                        setSuggestions={setSuggestions}
                        mapPin={mapPin}
                        isDesktop={isDesktopSize}
                      />
                    </div> */}
          </div>
          <div ref={listRef} className="h-full gap-10 overflow-y-auto overscroll-auto  md:pt-0">
            <FindStore
              storeData={filteredStores ?? storeData}
              selectedPlace={selectedPlace}
              handleSelectedPlace={handelSelectedPlace}
              userLocation={userLocation}
              activeTab={activeTab}
              badges={badges}
              amplienceStores={amplienceStores}
              getStoreBadges={getStoreBadges}
              isDesktopSize={isDesktopSize}
              isMobile={isMobile}
              selectedLocale={selectedLocale}
              clickAndCollect={clickAndCollect}
              query={queryStore}
              setQuery={setQueryStore}
              suggestions={suggestions}
              setSuggestions={setSuggestions}
              handleChange={handleChange}
              getSVGElement={getSVGElement}
              isDesktop={isDesktopSize}
              nearbyStores={nearbyStores}
              setNearByStores={setNearbyStores}
              isSmallMobile={isSmallMobile}
              currentLocationClicked={currentLocationClicked}
              citySearch={isCitySearch}
              storeTimingsData={storeTimingsData}
            />
          </div>
        </div>

        {/* Right side: NeighborhoodDiscovery */}
        {storeData && (filteredStores ?? storeData) && (
          <div
            className={`h-full ${
              activeTab === 'map' ? 'w-full' : isDesktopSize ? 'w-[1486px]' : isTablet ? 'w-[590px]' : 'hidden'
            }`}
          >
            <NeighborhoodDiscovery
              selectedPlace={selectedPlace}
              storeData={filteredStores ?? storeData}
              handleSelectedPlace={handelSelectedPlace}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              userLocation={userLocation}
              setUserLocation={setUserLocation}
              query={query}
              originalSearchQuery={originalSearchQuery?.current}
              setSearchInput={setSearchInput}
              searchInput={searchInput}
              setPreservedQuery={setPreservedQuery}
              preservedQuery={preservedQuery}
              setMapCenter={setMapCenter}
              mapCenter={mapCenter}
              mapZoom={mapZoom}
              setMapZoom={setMapZoom}
              badges={badges}
              amplienceStores={amplienceStores}
              getStoreBadges={getStoreBadges}
              isUltraLargeTablet={isTablet}
              isMobile={isMobile}
              selectedLocale={selectedLocale}
              getSVGElement={getSVGElement}
              clickAndCollect={clickAndCollect}
              isCitySearch={isCitySearch}
              setCurrentLocationClicked={setCurrentLocationClicked}
              setSlectedPlace={setSelectedPlace}
              storeTimingsData={storeTimingsData}
            />
          </div>
        )}
      </div>

      <div className={`mt-20 md:mt-45 md:flex-row `}>
        <InStoreService cardData={cardData} />
      </div>

      {/* Always show Stores List */}
      <div>
        <StoresList
          storeData={storeData?.map((store) => ({
            name: store?.name,
            url: store?.name,
            latitude: store?.latitude,
            longitude: store?.longitude,
          }))}
          handleSelectedPlace={handelSelectedPlace}
        />
      </div>
    </>
  );
};

export default StoreFinder;
